import styled, { keyframes } from 'styled-components';

import { simpleDateAtTime } from 'lib/date';
import Workflow from 'models/Workflow';
import { BodySmall, Label, LabelBold } from 'styles/typography';

interface Props {
  workflows: Workflow[];
  // usePortalName's resolver, same contract as taskCards — the server owns portal names.
  portalName: (sourceSystem?: string | null) => string;
}

/**
 * The runs in flight that nobody drives (OLIO-3130's counterpart to re-run: redispatch takes a
 * card OFF the tasks list, and this is what says where it went). Rows are deliberately inert —
 * not a button, no slideout: a headless run in flight has nothing for a person to do, and the
 * one gesture this surface promises is that rows appear and remove themselves on their own.
 */
export default function RunningTasksStrip({ workflows, portalName }: Props) {
  if (workflows.length === 0) return null;

  return (
    <Strip data-testid='running-tasks-strip'>
      <Heading>Running automatically</Heading>
      {workflows.map((workflow) => {
        const name = portalName(workflow.valueFor('source_system')?.value);
        return (
          <Row key={workflow.id} data-testid='running-task-card'>
            <Spinner aria-hidden />
            <Content>
              <Title>{workflow.patientName}</Title>
              <Detail>
                Retrieving from {name} automatically
                {workflow.createdAt && ` • started ${simpleDateAtTime(workflow.createdAt)}`}
              </Detail>
            </Content>
            {name && <SourceBadge>{name}</SourceBadge>}
          </Row>
        );
      })}
    </Strip>
  );
}

const Strip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Heading = styled(Label)`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.black50};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px dashed ${({ theme }) => theme.colors.black25};
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// CircularProgress hardcodes white on three sides, which disappears on a white row — this one
// carries the row's own muted palette instead.
const Spinner = styled.span`
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.black10};
  border-left-color: ${({ theme }) => theme.colors.blue100};
  animation: ${spin} 0.85s infinite linear;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Title = styled(LabelBold)`
  font-size: 14px;
`;

const Detail = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const SourceBadge = styled(BodySmall)`
  flex: 0 0 auto;
  padding: 4px 8px;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  background-color: ${({ theme }) => theme.colors.black10};
  color: ${({ theme }) => theme.colors.black75};
`;
